import * as React from 'react';
import config from '../config/config';
import { useNavigate } from 'react-router-dom';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';

import { Input } from './../components/form/Input';
import { MaskedTextBox } from './../components/form/MaskedTextBox';
import { Upload } from './../components/form/Upload';
import { Signature } from './../components/form/Signature';
import { DropDownList } from './../components/form/DropDownList';
import { Switch } from '@progress/kendo-react-inputs';
import { Loader } from '@progress/kendo-react-indicators';

import {
    requiredValidator,
    emailValidator,
    phoneValidator
} from './../validators';

// ────────────────────────────────────────────────────────────
// App-weite Notification-Funktion holen
// ────────────────────────────────────────────────────────────
import { AppContext } from '../AppContext';

const colorOptions = [
    { label: 'Braun', value: '#A52A2A' },
    { label: 'Tomate', value: '#FF6347' },
    { label: 'Orange', value: '#FFA500' },
    { label: 'Grün', value: '#9ACD32' },
    { label: 'Limette', value: '#90EE90' },
    { label: 'Blau', value: '#00BFFF' },
    { label: 'Navy', value: '#000080' },
    { label: 'Violet', value: '#6A5ACD' },
    { label: 'Pink', value: '#FF69B4' },
    { label: 'Sand', value: '#F4A460' },
    { label: 'Grau', value: '#778899' },
    { label: 'Schokolade', value: '#D2691E' }
];

const CreateUsers = () => {
    const navigate = useNavigate();
    const { showNotification } = React.useContext(AppContext);

    /* ── UI-States ─────────────────────────────────────────── */
    const [loading, setLoading] = React.useState(false);
    const [isInPublicDirectory, setIsInPublicDirectory] = React.useState(true);
    const [selectedColor, setSelectedColor] = React.useState(null);
    const [autoAvatar, setAutoAvatar] = React.useState(true); 
    const [externalEmployee, setExternalEmployee] = React.useState(false);

    /* ── Handler ───────────────────────────────────────────── */
    const handleActiveChange   = (e) => setIsInPublicDirectory(e.value);
    const handleAutoChange     = (e) => setAutoAvatar(e.value);
    const handleExternalChange = (e) => setExternalEmployee(e.value);

    /* ── Submit ────────────────────────────────────────────── */
    const onSubmit = async (dataItem) => {
        try {
            setLoading(true);

            const formData = new FormData();
            const userID = `U-${Date.now()}`;

            // Dateien (nur wenn Auto-Avatar aus)
            if (!autoAvatar && dataItem.avatar?.[0]?.getRawFile) {
                formData.append('avatar', dataItem.avatar[0].getRawFile());
            }
            if (dataItem.signature?.[0]?.getRawFile) {
                formData.append('signature', dataItem.signature[0].getRawFile());
            }

            // Text-Felder
            formData.append('Benutzername', `${dataItem.firstName} ${dataItem.lastName}`);
            formData.append('BenutzerID', userID);
            formData.append('Vorname', dataItem.firstName);
            formData.append('Nachname', dataItem.lastName);
            formData.append('EmailAdresse', dataItem.email);
            formData.append('Telefonnummer', dataItem.phoneNumber);
            formData.append('Passwort', dataItem.password);

            formData.append('Active', isInPublicDirectory);
            formData.append('UserColorCode', selectedColor?.value || '');
            formData.append('AutoAvatar', autoAvatar);
            formData.append('ExternalEmployee', externalEmployee);

            const resp = await fetch(`${config.host}/users`, {
                method: 'POST',
                body: formData
            });

            if (!resp.ok) {
                const err = await resp.json();
                showNotification('error', `Fehler: ${err.error}`);
                return;
            }

            showNotification('success', 'Benutzer erfolgreich erstellt!');
            navigate('/users');
        } catch (err) {
            console.error('Fehler beim Erstellen des Benutzers:', err);
            showNotification('error', 'Fehler beim Erstellen des Benutzers.');
        } finally {
            setLoading(false);
        }
    };

    /* ── Cancel ─────────────────────────────────────────────── */
    const onCancelClick = () => navigate('/users');

    /* ── Loader Screen ─────────────────────────────────────── */
    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    textAlign: 'center'
                }}
            >
                <Loader size="large" type="infinite-spinner" />
                <p>Speichern – bitte warten…</p>
            </div>
        );
    }

    /* ── Form ──────────────────────────────────────────────── */
    return (
        <div id="CreateUsers" className="profile-page main-content">
            <div className="card-container">
                <div className="card-component">
                    <Form
                        onSubmit={onSubmit}
                        render={(fp) => (
                            <FormElement horizontal style={{ maxWidth: 700 }}>
                                <Field
                                    id="avatar"
                                    name="avatar"
                                    component={Upload}
                                    hideUploadButton={autoAvatar}
                                    validator={!autoAvatar && requiredValidator}
                                />

                                <Field
                                    id="firstName"
                                    name="firstName"
                                    label="Vorname"
                                    component={Input}
                                    validator={requiredValidator}
                                />
                                <Field
                                    id="lastName"
                                    name="lastName"
                                    label="Nachname"
                                    component={Input}
                                    validator={requiredValidator}
                                />
                                <Field
                                    id="email"
                                    name="email"
                                    type="email"
                                    label="E-Mail"
                                    component={Input}
                                    validator={emailValidator}
                                />
                                <Field
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    label="Telefonnummer"
                                    component={MaskedTextBox}
                                    validator={phoneValidator}
                                />

                                {/* Farbdropdown */}
                                <DropDownList
                                    label="Farbe"
                                    id="UserColorCode"
                                    name="UserColorCode"
                                    data={colorOptions}
                                    component={DropDownList}
                                    textField="label"
                                    dataItemKey="value"
                                    value={selectedColor}
                                    onChange={(e) => setSelectedColor(e.target.value)}
                                />

                                {/* --- Switch-Block --- */}
                                <div style={{ marginTop: 20, marginLeft: 85 }}>
                                    <label style={{ marginRight: 15 }}>Benutzer aktiv:</label>
                                    <Switch
                                        checked={isInPublicDirectory}
                                        onChange={handleActiveChange}
                                    />
                                </div>

                                <div style={{ marginTop: 10, marginLeft: 55 }}>
                                    <label style={{ marginRight: 14 }}>Dynamisches Profil:</label>
                                    <Switch
                                        checked={autoAvatar}
                                        onChange={handleAutoChange}
                                    />
                                </div>

                                {autoAvatar && (
                                    <div style={{ marginTop: 10, marginLeft: 55 }}>
                                        <label style={{ marginRight: 11 }}>Externer Mitarbeiter:</label>
                                        <Switch
                                            checked={externalEmployee}
                                            onChange={handleExternalChange}
                                        />
                                    </div>
                                )}

                                <Field
                                    id="password"
                                    name="password"
                                    type="password"
                                    label="Passwort"
                                    component={Input}
                                    validator={requiredValidator}
                                />

                                <Field
                                    id="signature"
                                    name="signature"
                                    label="Unterschrift"
                                    component={Signature}
                                    validator={requiredValidator}
                                />

                                <hr />
                                <div className="k-form-buttons">
                                    <Button onClick={onCancelClick}>Abbrechen</Button>
                                    <Button
                                        themeColor="primary"
                                        type="submit"
                                        disabled={!fp.allowSubmit || loading}
                                    >
                                        Speichern
                                    </Button>
                                </div>
                            </FormElement>
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default CreateUsers;
